import { createSlice } from "@reduxjs/toolkit";
/** Decode user details directly from a JWT access token. */ /** Decode a base64url (or standard base64) string. */ function decodeBase64Url(input) {
    // Convert base64url → standard base64 and restore padding.
    const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - base64.length % 4) % 4);
    return atob(padded);
}
function decodeCurrentUser(accessToken) {
    try {
        const payload = JSON.parse(decodeBase64Url(accessToken.split(".")[1]));
        if (!payload.user) {
            return null;
        }
        return {
            user: payload.user,
            roles: payload.roles ?? [],
            permissions: payload.permissions ?? [],
            mustChangePassword: payload.must_change_password ?? false
        };
    } catch  {
        return null;
    }
}
const getInitialState = ()=>{
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    // Try to decode user details from the stored JWT (works for local HS512
    // tokens whose payload contains user/roles/permissions).  If that fails
    // (e.g. Keycloak RS256 tokens with a different payload shape), fall back
    // to the separately-persisted currentUser from localStorage.
    let currentUser = accessToken ? decodeCurrentUser(accessToken) : null;
    if (!currentUser) {
        try {
            const stored = localStorage.getItem("currentUser");
            if (stored) {
                currentUser = JSON.parse(stored);
            }
        } catch  {
        // corrupt JSON — ignore
        }
    }
    const isLoggedIn = !!accessToken && !!refreshToken && currentUser !== null;
    if (!isLoggedIn) {
        // Ensure localStorage is clean so the next page load starts fresh.
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("currentUser");
        localStorage.removeItem("oidcPublicConfig");
    }
    return {
        accessToken: isLoggedIn ? accessToken : null,
        refreshToken: isLoggedIn ? refreshToken : null,
        isLoggedIn,
        currentUser
    };
};
const initialState = getInitialState();
const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        // Handles a new successful login or token refresh
        authTokenChange: (state, action)=>{
            const { accessToken, refreshToken } = action.payload;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.isLoggedIn = true;
            // Persist to localStorage
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
        },
        // Clears all authentication data on logout
        logoutUser: (state)=>{
            state.accessToken = null;
            state.refreshToken = null;
            state.isLoggedIn = false;
            state.currentUser = null;
            // Clear localStorage
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("currentUser");
            localStorage.removeItem("oidcPublicConfig");
        },
        // Set user details after login
        setUserDetails: (state, action)=>{
            state.currentUser = action.payload;
            localStorage.setItem("currentUser", JSON.stringify(action.payload));
        }
    }
});
export const { authTokenChange, logoutUser, setUserDetails } = authSlice.actions;
export default authSlice.reducer;
